/**
 * modePresets.schema — Zod mirror of the persisted-layout payload contract.
 *
 * Cycle 101 sprint-3 Task 3.3 (sprint.md:307, SDD §4.2 lines 269-293).
 *
 * Mirror-of-TypeScript contract:
 *   * `ModulePlacement`     (SDD:277-285) → `ModulePlacementSchema`
 *   * `StoredModeLayoutV1_0` (SDD:288-293) → `StoredModeLayoutSchema`
 *   * `ModeId`              (SDD:275)     → `ModeIdSchema` (5-enum)
 *   * `ModuleId`            (moduleRegistry.ts) → `ModuleIdSchema` (registry-driven enum)
 *
 * Runtime hydration in `useModeCanvasLayout` runs `.safeParse()` against
 * `StoredModeLayoutSchema`. Failure ⇒ fall back to preset default per
 * SDD §7.3 Zod-validation-failure branch.
 *
 * Refinements (beyond plain z.object shape):
 *   * `x` must be 0..11 (12-col grid)
 *   * `w` must be 1..12 and `x + w <= 12` (no column overflow at edge)
 *   * `h` must be >= 1 (140px row unit; unbounded upward)
 *   * `placements[].id` must be unique within a layout
 *   * `updatedAt` must be a parseable ISO-8601 timestamp
 *   * `presetVersion` must be a non-negative integer
 */

import { z } from 'zod';
import { MODULE_IDS, type ModuleId } from './moduleRegistry';

// ── ModeId — 5-enum matching WorkspaceModeId in modeRegistry ────────────

export const ModeIdSchema = z.enum([
  'world',
  'geopolitical',
  'finance',
  'maritime',
  'investigations',
]);

export type ModeIdFromSchema = z.infer<typeof ModeIdSchema>;

// ── ModuleId — registry-driven enum ─────────────────────────────────────
// z.enum wants a [string, ...string[]] literal tuple. Cast is safe because
// MODULE_IDS is readonly non-empty.
export const ModuleIdSchema = z.enum(MODULE_IDS as unknown as [ModuleId, ...ModuleId[]]);

// ── ModulePlacement ─────────────────────────────────────────────────────

export const ModulePlacementSchema = z
  .object({
    id: z.string().min(1, 'placement id must be non-empty'),
    moduleId: ModuleIdSchema,
    x: z.number().int().min(0).max(11),
    y: z.number().int().min(0),
    w: z.number().int().min(1).max(12),
    h: z.number().int().min(1),
    z: z.number().int().optional(),
  })
  .refine((p) => p.x + p.w <= 12, {
    message: 'placement overflows 12-col grid (x + w > 12)',
    path: ['w'],
  });

export type ModulePlacementFromSchema = z.infer<typeof ModulePlacementSchema>;

// ── StoredModeLayoutV1_0 ───────────────────────────────────────────────

export const StoredModeLayoutSchema = z
  .object({
    mode: ModeIdSchema,
    presetVersion: z.number().int().min(0),
    placements: z.array(ModulePlacementSchema),
    updatedAt: z
      .string()
      .refine((s) => !Number.isNaN(Date.parse(s)), {
        message: 'updatedAt must be a parseable ISO-8601 timestamp',
      }),
  })
  .refine(
    (layout) => {
      const ids = new Set<string>();
      for (const p of layout.placements) {
        if (ids.has(p.id)) return false;
        ids.add(p.id);
      }
      return true;
    },
    { message: 'placements contain duplicate ids', path: ['placements'] }
  );

export type StoredModeLayoutFromSchema = z.infer<typeof StoredModeLayoutSchema>;
